import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, MessageCircle, Navigation } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { ContactForm } from '../components/ui/ContactForm';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionH1 = motion.h1;
const MotionH2 = motion.h2;
const MotionP = motion.p;

export default function Contact() {
  const contactInfo = {
    address: {
      kandla: "Survey No. 10 Neelkanth vadi, Terepanth Road, Village Kidana, Behind KSEZ, Gandhidham- Kutch 370201",
      mundra: "Survey no. 75/1 opp. SEZ Adani port Road, Nr. GIDC Mundra (Kutch)",
    },
    mapLinks: {
      kandla: "https://maps.app.goo.gl/nNQeDXWze6SPbH3L9",
      mundra: "https://www.google.com/maps/place/NAVDEEP+Shipping+Pvt+Ltd/@22.8125427,69.6798729,17z/data=!3m1!4b1!4m6!3m5!1s0x3950d545b750261f:0x1cea574de6748133!8m2!3d22.8125427!4d69.6824478!16s%2Fg%2F11q94xrscx?entry=ttu&g_ep=EgoyMDI1MDYyMy4yIKXMDSoASAFQAw%3D%3D"
    },
    distances: {
      kandla: {
        km: "15 km",
        miles: "9 miles",
        description: "Kandla Port To Mega Marine Container Terminals"
      },
      mundra: {
        km: "11 km", 
        miles: "6.8 miles",
        description: "Mundra Port To Mega Marine Container Terminals"
      }
    },
    phones: ["+919687990249", "+919979533765"],
    email: "info@megamarinecontainer.com", // Company email
    hours: {
      weekdays: "9:00 AM - 6:00 PM",
      saturday: "10:00 AM - 4:00 PM",
      sunday: "Closed",
      operations: "24/7 Container Yard Operations"
    }
  };

  return (
    <div>      {/* Hero Section */}
      <div className="relative py-24 bg-primary">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Contact us"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">            <MotionH1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-[1.1] tracking-tight"
            >
              Contact Us
            </MotionH1>
            <MotionP
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl leading-[1.4]"
            >
              Get in touch with our team for all your container yard needs
            </MotionP>
          </div>
        </div>      </div>

      {/* Contact Form and Info */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-16">
          <MotionDiv
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-10"
          >
            <div>
              <h2 className="text-3xl font-bold mb-10 text-gray-900 dark:text-white leading-[1.2] tracking-tight">Contact Information</h2>
              
              <div className="space-y-8">                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-white leading-[1.3]">Our Locations</h3>
                    <div className="space-y-5">
                      <div>                        <p className="font-medium text-primary mb-2 leading-[1.4]">Kandla Yard:</p>
                        <p className="text-gray-700 dark:text-gray-300 leading-[1.5]">{contactInfo.address.kandla}</p>
                      </div>
                      <div>
                        <p className="font-medium text-primary mb-2 leading-[1.4]">Mundra Yard:</p>
                        <p className="text-gray-700 dark:text-gray-300 leading-[1.5]">{contactInfo.address.mundra}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div className="flex-1">                    <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-white leading-[1.3]">Phone</h3>
                    <div className="space-y-1">
                      {contactInfo.phones.map((num, idx) => (
                        <a
                          key={num}
                          href={`tel:${num}`}
                          className="block text-gray-700 dark:text-gray-300 hover:text-primary text-lg leading-[1.4]"
                        >
                          {num.replace('+91', '+91 ')}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-white leading-[1.3]">Email</h3>
                    <a href={`mailto:${contactInfo.email}`} className="text-gray-700 dark:text-gray-300 hover:text-primary text-lg leading-[1.4]">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900 dark:text-white leading-[1.3]">Business Hours</h3>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                      <li className="flex justify-between leading-[1.5]">
                        <span>Monday - Friday:</span>
                        <span className="text-primary font-medium">{contactInfo.hours.weekdays}</span>
                      </li>
                      <li className="flex justify-between leading-[1.5]">
                        <span>Saturday:</span>
                        <span className="text-primary font-medium">{contactInfo.hours.saturday}</span>
                      </li>
                      <li className="flex justify-between leading-[1.5]">
                        <span>Sunday:</span>
                        <span className="text-primary font-medium">{contactInfo.hours.sunday}</span>
                      </li>
                      <li className="mt-4 font-medium leading-[1.5]">
                        {contactInfo.hours.operations}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>            </div>
            
            <div className="bg-gray-50 dark:bg-navy-light p-8 rounded-lg">
              <div className="flex items-center gap-4 mb-5">
                <MessageCircle className="h-6 w-6 text-primary" />                <h3 className="font-semibold text-lg text-gray-900 dark:text-white leading-[1.3]">Quick Response Guarantee</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-[1.5]">
                We value your time and are committed to providing prompt responses to all inquiries. 
                Our team will get back to you within 24 hours on business days.
              </p>
            </div>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900 dark:text-white leading-[1.2] tracking-tight">Get In Touch</h2>
            <ContactForm />
          </MotionDiv>
        </div>
      </Section>      {/* Map Section */}
      <Section className="bg-gray-50 dark:bg-navy">
        <div className="max-w-3xl mx-auto text-center mb-16">          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white leading-[1.2] tracking-tight">Our Locations</h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 leading-[1.4]">
            Visit us at our strategically located container yards near major ports
          </p>
        </div>
        
        {/* Yard to Port Distances */}
        <div className="max-w-4xl mx-auto mb-16">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-blue-50 dark:bg-blue-900/20 p-8 rounded-lg border border-blue-200 dark:border-blue-800"
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              <Navigation className="h-7 w-7 text-primary" />
              <h3 className="font-bold text-2xl text-gray-900 dark:text-white leading-[1.2] tracking-tight">Yard to Port Distances</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-l-4 border-primary pl-8 bg-white dark:bg-navy/50 p-6 rounded-r-lg">                <h4 className="font-semibold text-primary mb-3 text-lg leading-[1.3]">{contactInfo.distances.mundra.description}</h4>
                <p className="text-gray-700 dark:text-gray-300 text-xl leading-[1.4]">
                  <span className="font-bold text-primary">{contactInfo.distances.mundra.km}</span> or <span className="font-bold text-primary">{contactInfo.distances.mundra.miles}</span>
                </p>
              </div>
              <div className="border-l-4 border-primary pl-8 bg-white dark:bg-navy/50 p-6 rounded-r-lg">
                <h4 className="font-semibold text-primary mb-3 text-lg leading-[1.3]">{contactInfo.distances.kandla.description}</h4>
                <p className="text-gray-700 dark:text-gray-300 text-xl leading-[1.4]">
                  <span className="font-bold text-primary">{contactInfo.distances.kandla.km}</span> or <span className="font-bold text-primary">{contactInfo.distances.kandla.miles}</span>
                </p>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-8 font-medium leading-[1.5]">
              Strategically located for efficient container transportation and logistics
            </p>
          </MotionDiv>
        </div>          <div className="grid md:grid-cols-2 gap-10">
          {[
            { 
              name: "Kandla Yard", 
              address: contactInfo.address.kandla,
              distance: contactInfo.distances.kandla,
              details: {
                surveyNo: "Survey No. 10",
                area: "Neelkanth vadi, Terepanth Road",
                village: "Village Kidana",
                landmark: "Behind KSEZ",
                city: "Gandhidham",
                district: "Kutch",
                pincode: "370201",
                state: "Gujarat"
              }
            },
            { 
              name: "Mundra Yard", 
              address: contactInfo.address.mundra,
              distance: contactInfo.distances.mundra,
              details: {
                surveyNo: "Survey no. 75/1",
                area: "Opp. SEZ Adani port Road",
                landmark: "Nr. GIDC",
                city: "Mundra",
                district: "Kutch",
                pincode: "",
                state: "Gujarat"
              }
            }
          ].map((location, index) => (
            <MotionDiv 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              className="bg-white dark:bg-navy-light rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="h-52 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 relative overflow-hidden">
                {/* Embedded Google Map with Pin */}
                <iframe
                  title={`Google Map - ${location.name}`}
                  src={
                    location.name === "Kandla Yard"
                      ? "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3719.964964889624!2d70.1359640751956!3d23.07096477913906!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3950bfcf7e2e7e2d%3A0x6e2e7e2e7e2e7e2e!2sSurvey%20No.%2010%2C%20Neelkanth%20vadi%2C%20Terepanth%20Road%2C%20Village%20Kidana%2C%20Behind%20KSEZ%2C%20Gandhidham-%20Kutch%20370201!5e0!3m2!1sen!2sin!4v1710000000000!5m2!1sen!2sin"
                      : "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3719.964964889624!2d69.6798729!3d22.8125427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3950d545b750261f%3A0x1cea574de6748133!2sNAVDEEP%20Shipping%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1710000000001!5m2!1sen!2sin"
                  }
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '208px', width: '100%' }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white leading-[1.3]">{location.name}</h3>
                  <div className="bg-primary/10 text-primary px-3 py-2 rounded text-sm font-medium">
                    {location.distance.miles}
                  </div>
                </div>
                
                {/* Extended address details */}
                <div className="space-y-4 mb-8">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>                      <span className="text-gray-500 dark:text-gray-400 leading-[1.4]">Survey:</span>
                      <p className="font-medium text-gray-900 dark:text-white leading-[1.4]">{location.details.surveyNo}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 leading-[1.4]">City:</span>
                      <p className="font-medium text-gray-900 dark:text-white leading-[1.4]">{location.details.city}</p>
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm leading-[1.4]">Location:</span>
                    <p className="text-gray-700 dark:text-gray-300 leading-[1.5]">{location.details.area}</p>
                  </div>
                  
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm leading-[1.4]">Landmark:</span>
                    <p className="text-gray-700 dark:text-gray-300 leading-[1.5]">{location.details.landmark}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 leading-[1.4]">District:</span>
                      <p className="font-medium text-gray-900 dark:text-white leading-[1.4]">{location.details.district}</p>
                    </div>
                    {location.details.pincode && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 leading-[1.4]">Pincode:</span>
                        <p className="font-medium text-gray-900 dark:text-white leading-[1.4]">{location.details.pincode}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-medium leading-[1.5]">
                      {location.distance.description}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <a
                    href={location.name === "Kandla Yard" ? contactInfo.mapLinks.kandla : contactInfo.mapLinks.mundra}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-primary text-white hover:bg-primary/90 px-5 py-3 rounded-md font-medium transition-colors duration-300 inline-flex items-center justify-center gap-2 text-sm leading-[1.4]"
                  >
                    <MapPin className="h-4 w-4" /> View on Map
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 px-5 py-3 rounded-md font-medium transition-colors duration-300 inline-flex items-center justify-center gap-2 text-sm leading-[1.4]"
                  >
                    <Navigation className="h-4 w-4" /> Get Directions
                  </a>
                </div>
              </div>
            </MotionDiv>
          ))}
        </div>
      </Section>      {/* FAQs */}
      <Section
        title="Frequently Asked Questions"
        subtitle="Find quick answers to common questions about our services"
      >
        <div className="max-w-3xl mx-auto">
          {[
            { 
              question: "What are your container yard operating hours?", 
              answer: "Our container yards operate 24/7, while our administrative office is open Monday to Friday from 9:00 AM to 6:00 PM and Saturday from 10:00 AM to 4:00 PM." 
            },
            { 
              question: "How quickly can you process container repairs?", 
              answer: "Standard repairs are typically completed within 24-48 hours, depending on the extent of damage. Emergency repairs can be expedited upon request." 
            },
            { 
              question: "Do you provide container transportation to and from ports?", 
              answer: "We offer on-site transportation within our yard premises. For port to yard transportation, we can recommend trusted transport partners." 
            },
            { 
              question: "How secure are your container storage facilities?", 
              answer: "Our yards feature 24/7 CCTV surveillance, secured perimeter, controlled access, and regular security patrols to ensure maximum protection of all stored containers." 
            }
          ].map((faq, index) => (
            <MotionDiv
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="mb-8 pb-8 border-b border-gray-200 dark:border-gray-700 last:border-0"
            >              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white leading-[1.3]">{faq.question}</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-[1.5]">{faq.answer}</p>
            </MotionDiv>
          ))}
        </div>
      </Section>

      {/* Call to Action */}
      <Section className="bg-primary text-white">
        <div className="max-w-3xl mx-auto text-center">
          <MotionH2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}            className="text-3xl md:text-4xl font-bold mb-8 leading-[1.2] tracking-tight"
          >
            Ready to Discuss Your Container Needs?
          </MotionH2>
          <MotionP
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl mb-10 text-gray-200 leading-[1.4]"
          >
            Our team is ready to provide personalized solutions for your container handling, storage, and maintenance requirements.
          </MotionP>
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            {contactInfo.phones.map((num) => (
              <a
                key={num}
                href={`tel:${num}`}
                className="bg-white text-primary hover:bg-gray-100 px-10 py-4 rounded-md font-medium transition-colors duration-300 inline-flex items-center justify-center gap-3 leading-[1.4]"
              >
                <Phone className="h-5 w-5" /> {num.replace('+91', '+91 ')}
              </a>
            ))}
          </MotionDiv>
        </div>
      </Section>
    </div>
  );
}


